import { FormEvent, useState } from "react"
import {
    IRestaurantMenuLanguage,
    setRestaurantMenuLanguages,
} from "@/shared/api/restaurant-menus"
import notify from "@/shared/lib/notify"
import { ConfirmModal } from "@/shared/ui/confirm-modal"
import { getApiErrorMessage } from "../lib/get-api-error-message"
import { RestaurantMenuLanguageDraft } from "../model/types"

interface MenuLanguagesTabProps {
    menuId: string
    languages: IRestaurantMenuLanguage[]
    onChange: (languages: IRestaurantMenuLanguage[]) => void
}

const emptyDraft: RestaurantMenuLanguageDraft = {
    code: "",
    name: "",
}

const normalizeLanguages = (languages: IRestaurantMenuLanguage[]): RestaurantMenuLanguageDraft[] => {
    return languages
        .filter((language): language is RestaurantMenuLanguageDraft => Boolean(language.code && language.name))
        .map((language) => ({
            code: language.code,
            name: language.name,
        }))
}

export const MenuLanguagesTab = ({ menuId, languages, onChange }: MenuLanguagesTabProps) => {
    const [draft, setDraft] = useState(emptyDraft)
    const [formError, setFormError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [languageToDelete, setLanguageToDelete] = useState<IRestaurantMenuLanguage | null>(null)

    const saveLanguages = async (nextLanguages: RestaurantMenuLanguageDraft[]) => {
        try {
            setIsSubmitting(true)
            setFormError(null)
            const response = await setRestaurantMenuLanguages(menuId, { languages: nextLanguages })
            onChange(response.data)
            notify.success("Языки сохранены")
        } catch (error) {
            setFormError(getApiErrorMessage(error))
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault()

        const nextLanguage = {
            code: draft.code.trim(),
            name: draft.name.trim(),
        }

        if (!nextLanguage.code || !nextLanguage.name) {
            setFormError("Заполните код и название языка")
            return
        }

        if (languages.some((language) => language.code === nextLanguage.code)) {
            setFormError("Язык с таким кодом уже есть в списке")
            return
        }

        await saveLanguages([...normalizeLanguages(languages), nextLanguage])
        setDraft(emptyDraft)
    }

    const handleConfirmDelete = async () => {
        if (!languageToDelete?.code) {
            return
        }

        if (languages.length === 1) {
            setLanguageToDelete(null)
            setFormError("У меню должен остаться хотя бы один язык")
            return
        }

        const nextLanguages = normalizeLanguages(languages).filter((language) => language.code !== languageToDelete.code)
        await saveLanguages(nextLanguages)
        setLanguageToDelete(null)
    }

    return (
        <div className="flex flex-col gap-4">
            {languages.length === 0 && (
                <div className="info_panel">Языки не настроены</div>
            )}

            {languages.length > 0 && (
                <div className="table_box">
                    <div className="overflow-auto">
                        <table className="react-table">
                            <thead>
                                <tr>
                                    <th>Код</th>
                                    <th>Название</th>
                                    <th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {languages.map((language, index) => (
                                    <tr key={`${language.code ?? "language"}-${index}`}>
                                        <td>{language.code || "—"}</td>
                                        <td>{language.name || "—"}</td>
                                        <td>
                                            <span title={languages.length === 1 ? "У меню должен остаться хотя бы один язык" : undefined}>
                                                <button
                                                    type="button"
                                                    disabled={languages.length === 1 || isSubmitting}
                                                    onClick={() => setLanguageToDelete(language)}
                                                    className="form_cancel_button !h-9 !w-auto px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Удалить
                                                </button>
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="rounded-lg border border-[#D9E3E1] p-4">
                <div className="mb-3 font-medium">Добавить язык</div>
                <div className="grid grid-cols-12 gap-3">
                    <input
                        value={draft.code}
                        onChange={(event) => setDraft((state) => ({ ...state, code: event.target.value }))}
                        className="input_field col-span-4"
                        placeholder="code"
                    />
                    <input
                        value={draft.name}
                        onChange={(event) => setDraft((state) => ({ ...state, name: event.target.value }))}
                        className="input_field col-span-5"
                        placeholder="Название"
                    />
                    <button type="submit" disabled={isSubmitting} className="form_handle_button col-span-3">
                        Добавить
                    </button>
                </div>
                {formError && <div className="mt-3 text-color-danger">{formError}</div>}
            </form>

            <ConfirmModal
                isOpen={Boolean(languageToDelete)}
                title="Удалить язык"
                message={`Язык "${languageToDelete?.name || languageToDelete?.code || ""}" будет удалён из меню. Продолжить?`}
                confirmText="Удалить"
                onConfirm={handleConfirmDelete}
                onCancel={() => setLanguageToDelete(null)}
            />
        </div>
    )
}
